import { type ValueEvent } from '@adornis/base/utilTypes.js';
import { RegistrationController } from '@adornis/bh-registration/client/bh-registration-controller.js';
import { checkWeatherUserMeetsEntryRequirementsQuery } from '@adornis/buchungsheld-db/api/product-entry-requirement.js';
import { UserStammdaten } from '@adornis/buchungsheld-db/entities/stammdaten.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { getPluginForScope } from '@adornis/buchungsheld-db/plugins/plugin.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { GenericFieldFormController } from '@adornis/cms/client/generic-field-formcontroller.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { logout } from '@adornis/users/client/loginFunctions.js';
import { registerUser } from '@adornis/users/db/a-user.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { combineLatest, distinctUntilChanged, filter, map, merge, of, switchMap } from 'rxjs';
import '../../components/bh-stammdaten-form';
import { BHRegistrationFlowSubPage } from './bh-page-registration-sub-page.js';
import {
  checkForAnyInvalidSelectedProducts,
  renderSelectedBundles,
  renderSelectedWorkshops,
} from './registration-globals.js';

@customElement('bh-page-registration-personal')
export class BHPageRegistrationPersonal extends BHRegistrationFlowSubPage {
  _institutionID$ = this._registrationController$.pipe(
    switchMap(regController => regController?.institutionID$ ?? of(null)),
    filter(Boolean),
    distinctUntilChanged(),
  );

  _savedstammdaten$ = merge(
    of(undefined),
    combineLatest([this._institutionID$, currentUser()]).pipe(
      switchMap(([institutionID, user]) =>
        user
          ? UserStammdaten.subscribeByUserAndInstitution(user._id, institutionID)(UserStammdaten.allFields)
          : of(undefined),
      ),
    ),
  ).pipe(
    map(it => (it ? it : undefined)),
    distinctUntilChanged(),
  );

  @state() private readonly _stammdaten = new RXController(
    this,
    this._savedstammdaten$.pipe(
      switchMap(savedStammdaten => {
        if (savedStammdaten) return of(savedStammdaten);
        return this._institutionID$.pipe(
          switchMap(async institutionID => {
            return UserStammdaten.createStammdatenForInstitutionID(institutionID, false);
          }),
        );
      }),
    ),
  );

  @state() private readonly _currentUserData = new RXController(
    this,
    combineLatest([
      RegistrationController.currentUser$,
      this._registrationController$.pipe(switchMap(regController => regController?.registrationData$ ?? of(null))),
    ]).pipe(map(([maybeUser, registrationData]) => maybeUser ?? registrationData?.user ?? new User({}))),
  );

  @state() private readonly _stammdatenController = new GenericFieldFormController(this, this._stammdaten.observable, {
    onSuccess: entity => {
      console.log(entity);
      for (const key of Object.keys((entity as any).doc)) {
        const value = entity[key];
        if (typeof value === 'string' && value.length > 300) {
          throw new ValidationError(this._translation.translate('REGISTRATION', 'input_too_long') as any, {
            key,
            translationKey: 'REGISTRATION.input_too_long',
          });
        }
      }
    },
  });

  @state() private readonly _userDataController = new FormController(this, this._currentUserData.observable, {
    disabledFields: RegistrationController.currentUser$.pipe(map(user => (user ? ({ email: 1 } as const) : {}))),
  });

  override backAction = async () => {
    if (this._stammdaten.value) this._registrationController.updateStammdaten(this._stammdaten.value);
    if (this._userDataController.document) this._registrationController.updateUser(this._userDataController.document);
    await this._registrationController.previous();
  };

  override nextAction = async () => {
    // set title only when stammdaten plugin contains title field
    const title = this._stammdatenController.document?.['title'];
    if (this._userDataController.document && title) this._userDataController.document.title = title;

    const stammdatenPlugin = getPluginForScope('registration::stammdaten', this._registrationController.institutionID);
    const stammdatenValidationError = stammdatenPlugin.customValidate?.(this._stammdatenController.document);
    if (stammdatenValidationError) {
      void XSnackbar.show(stammdatenValidationError);
      return;
    }

    const userData = await this._userDataController.validate();

    if (!userData) throw new Error('userData is undefined');
    if (!this._stammdatenController.document) throw new Error('stamdaten doc is undefined');

    const userExists = await User.getUserByEmailExists(userData.email)();

    if (userExists && !this._registrationController.stepInfo.userIsLoggedIn) {
      await XDialog.alert(this._translation.translate('field', 'emailAlreadyInUse'));
      return;
    }

    if (!userExists) {
      const userID = await registerUser({
        ...userData.toObject(),
        userClass: User,
        additionalFields: userData.toJSON(),
        loginAfterCreate: true,
      });
      this._stammdatenController.document.userID = userID;
      userData._id = userID;
    } else {
      await userData.save();
      this._stammdatenController.document.userID = userData._id;
    }

    // user should now exist
    const stammdaten = await this._stammdatenController.validate();
    if (!stammdaten) throw new Error('stammdaten are undefined');

    // this validation code should be in the registration::stammdaten::sd-plugin, but there it is not possible to access the email-address
    if (stammdatenPlugin.id === 'registration::stammdaten::sd') {
      const { SDInternOccupations } = await import(
        '@adornis/buchungsheld-db/plugins/stammdaten/sd-stammdaten-plugin.js'
      );
      const email = this._userDataController.document?.email;
      if (!email) throw new Error('should not happen');
      if (
        SDInternOccupations.includes((this._stammdatenController.document as any).occupation) &&
        !email.endsWith('.uni-frankfurt.de')
      ) {
        void XSnackbar.show(
          'Interne Mitarbeiter der Universität Frankfurt müssen sich mit ihrer universitären E-Mail Adresse anmelden (*.uni-frankfurt.de)',
        );
        return;
      }
    }

    // save stammdaten and user to controller if validation successful
    if (userData && stammdaten) {
      try {
        await stammdaten.save();
      } catch (e) {
        console.error(
          'Creating Stammdaten instead of saving, because we cannot determine if stammdaten were saved already or not. Ignore the above error log ("Could not find Entity to update.")',
        );
        await stammdaten.create();
      }

      // check if entry requirements are met this has to happen after stammdaten are saved
      const userID = this._registrationController.currentUser?._id;
      if (!userID) throw new Error('userID is undefined');
      for (const product of this._registrationController.selectedProducts) {
        try {
          await checkWeatherUserMeetsEntryRequirementsQuery(userID, product._id)();
        } catch (e) {
          void XSnackbar.show((e as any)?.message ?? this._translation.translate('err', 'unexpected'));
          return;
        }
      }

      await this._registrationController.next();
    }
  };

  override content() {
    if (this._currentUserData.isLoading || this._stammdaten.isLoading || !this._registrationController.registrationData)
      return html` <tr-loading-state></tr-loading-state> `;

    const productsAreValid = !checkForAnyInvalidSelectedProducts(
      this._registrationController.selectedProducts,
      this._registrationController.registrationData,
    );
    this.nextButtonDisabled = !productsAreValid;

    const workshopSelection = html`
      ${renderSelectedWorkshops(
        this,
        this._registrationController.selectedProducts,
        this._registrationController.registrationData,
      )}
      ${renderSelectedBundles(this, this._registrationController.selectedBundles ?? [])}
    `;

    const main = html`
      ${this._registrationController.stepInfo?.userIsLoggedIn === false
        ? html` <x-button @click=${() => this._registrationController.showLoginScreen()}>Login</x-button> <x-hr></x-hr>`
        : nothing}

      <x-flex ${css({ paddingTop: this.spacing.md })} space="sm">
        <x-flex space="sm">
          <x-flex space="xs">
            <tr-input
              flex
              placeholder="E-Mail*"
              ${this._userDataController.field('email')}
              @value-picked=${(e: ValueEvent<string>) => {
                // dirty hack to ensure non optional unique username
                this._userDataController.document.username = e.detail.value;
                this.requestUpdate();
              }}
              type="email"
            ></tr-input>
            <tr-input ${css({ display: 'none' })} ${this._userDataController.field('username')}></tr-input>
            ${this._registrationController.currentUser
              ? html`
                  <tr-button ${css({ alignSelf: 'flex-end' })} icon="logout" mode="text" @click=${() => logout()}>
                    ${this._translation.translate('PROFILE', 'cancle_booking')}
                  </tr-button>
                `
              : nothing}
          </x-flex>
          <tr-input
            placeholder=${this._translation.translate('field', 'firstName') + '*'}
            ${this._userDataController.field('firstName')}
          ></tr-input>
          <tr-input
            placeholder=${this._translation.translate('field', 'lastName') + '*'}
            ${this._userDataController.field('lastName')}
          ></tr-input>
        </x-flex>

        <bh-stammdaten-form
          .institutionID=${this._registrationController.institutionID}
          .user=${this._userDataController.document}
          .stammdaten=${this._stammdaten.observable}
          .stammdatenController=${this._stammdatenController}
        ></bh-stammdaten-form>
      </x-flex>
    `;

    return html`
      <!-- workshop selection -->
      ${workshopSelection}

      <!-- content -->
      ${main}
    `;
  }
}
